import { useEffect, useState, useCallback } from 'react';
import { generationAPI, logsAPI } from '../services/api';
import ArticleEditorModal from '../components/ArticleEditorModal';
import './Dashboard.css';

const formatDate = (value) => new Date(value).toLocaleString();

function Dashboard() {
    const [articles, setArticles] = useState([]);
    const [articlesLoading, setArticlesLoading] = useState(true);
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(true);
    const [editorArticleId, setEditorArticleId] = useState(null);

    const loadArticles = useCallback(async () => {
        setArticlesLoading(true);
        try {
            const response = await generationAPI.getArticles();
            const sorted = [...(response.data || [])].sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
            setArticles(sorted);
        } catch (error) {
            console.error('Failed to load articles:', error);
        } finally {
            setArticlesLoading(false);
        }
    }, []);

    const HIDDEN_LOG_ACTIONS = new Set(['generation_stage', 'real_photos_progress']);

    const loadLogs = useCallback(async () => {
        setLogsLoading(true);
        try {
            const response = await logsAPI.getAll();
            const filtered = (response.data || []).filter(
                (log) => !HIDDEN_LOG_ACTIONS.has(log.action)
            );
            const sorted = filtered.sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
            setLogs(sorted);
        } catch (error) {
            console.error('Failed to load logs:', error);
        } finally {
            setLogsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadArticles();
        loadLogs();
    }, [loadArticles, loadLogs]);

    useEffect(() => {
        const handleActivityUpdate = () => {
            loadArticles();
            loadLogs();
        };
        window.addEventListener('activity-update', handleActivityUpdate);
        return () => window.removeEventListener('activity-update', handleActivityUpdate);
    }, [loadArticles, loadLogs]);

    const downloadLogBundle = async (logId) => {
        try {
            const response = await logsAPI.download(logId);
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'generation_bundle.zip';
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download bundle:', error);
            alert('Download failed');
        }
    };

    const renderLogChips = (log) => {
        const meta = log.log_metadata || {};
        const chips = [];
        if (meta.article_id) chips.push({ label: 'Article', value: meta.article_id.slice(0, 8) });
        if (meta.ai_provider) chips.push({ label: 'Model', value: meta.ai_provider });
        if (meta.num_blocks) chips.push({ label: 'Blocks', value: meta.num_blocks });
        if (meta.word_count_target) chips.push({ label: 'Words', value: meta.word_count_target });
        if (meta.requested_images) chips.push({ label: 'Images', value: meta.requested_images });
        if (meta.enabled_style_ids && meta.enabled_style_ids.length) chips.push({ label: 'Styles', value: meta.enabled_style_ids.length });
        if (meta.prompt_overrides && Object.keys(meta.prompt_overrides).length) {
            chips.push({ label: 'Overrides', value: Object.keys(meta.prompt_overrides).length });
        }
        return chips;
    };

    return (
        <div className="dashboard">
            <div className="dashboard-header">
                <h1>Activity Log</h1>
                <p>Review every article, image, and model you have used.</p>
            </div>

            <div className="dashboard-grid">
                <div className="dashboard-panel">
                    <div className="panel-header">
                        <h2>Your Articles</h2>
                        <button className="btn-small" onClick={loadArticles}>Refresh</button>
                    </div>
                    {articlesLoading ? (
                        <div className="panel-loading">
                            <span className="spinner" />
                            <p>Loading articles...</p>
                        </div>
                    ) : articles.length === 0 ? (
                        <p>No articles generated yet.</p>
                    ) : (
                        <ul className="article-list">
                            {articles.map((article) => (
                                <li
                                    key={article.id}
                                    className="article-item"
                                >
                                    <div>
                                        <h3>{article.title || 'Untitled Article'}</h3>
                                        <div className="article-meta-row">
                                            <span>{formatDate(article.created_at)}</span>
                                            <span>{article.ai_provider}</span>
                                            {article.word_count && <span>{article.word_count} words</span>}
                                        </div>
                                    </div>
                                    <button className="btn-small" onClick={() => setEditorArticleId(article.id)}>
                                        View
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="dashboard-panel">
                    <div className="panel-header">
                        <h2>Generation Logs</h2>
                        <button className="btn-small" onClick={loadLogs}>Refresh</button>
                    </div>
                    {logsLoading ? (
                        <div className="panel-loading">
                            <span className="spinner" />
                            <p>Loading logs...</p>
                        </div>
                    ) : logs.length === 0 ? (
                        <p>No activity yet.</p>
                    ) : (
                        <div className="log-list">
                            {logs.map((log) => {
                                const chips = renderLogChips(log);
                                return (
                                    <div key={log.id} className="log-card">
                                        <div className="log-card-header">
                                            <span className="log-action">{log.action.replace('_', ' ')}</span>
                                            <span className="log-date">{formatDate(log.created_at)}</span>
                                        </div>
                                        {chips.length > 0 && (
                                            <div className="log-chips">
                                                {chips.map((chip, idx) => (
                                                    <span key={idx} className="log-chip">
                                                        <strong>{chip.label}:</strong> {chip.value}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <div className="log-actions">
                                            {log.log_metadata?.article_id && (
                                                <>
                                                    <button
                                                        className="btn-small"
                                                        onClick={() => setEditorArticleId(log.log_metadata.article_id)}
                                                    >
                                                        View Article
                                                    </button>
                                                    <button
                                                        className="btn-small"
                                                        onClick={() => downloadLogBundle(log.id)}
                                                    >
                                                        Download Zip
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {editorArticleId && (
                <ArticleEditorModal
                    key={editorArticleId}
                    articleId={editorArticleId}
                    onClose={() => setEditorArticleId(null)}
                />
            )}
        </div>
    );
}

export default Dashboard;
